import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../../config/firebase';
import { logger } from '../../utils/logger';
import {
  DailyKickoffResponse,
  AdjustScheduleResponse,
  ParseScheduleResponse,
  ApplyScheduleResponse,
  ApplySuggestionResponse,
  ScheduleOperation,
  TaskSuggestion
} from '../../types/morningKickoff';

// 只取 seconds 做比對，不依賴完整 Timestamp 物件（支援序列化格式）
interface FirestoreTimestamp {
  seconds: number;
}

export interface MorningBriefCacheData {
  narrative: string;
  taskSuggestions: TaskSuggestion[];
  suggestionOverallReason: string | null;
  acceptedSuggestionKeys: string[];
}

/**
 * 直讀 Firestore cache，實現 Morning Brief cache-first 加速
 * @returns cache 資料，或 null（MISS / Stale / 讀取失敗）
 */
export const getMorningBriefCache = async (
  userId: string,
  date: string
): Promise<MorningBriefCacheData | null> => {
  try {
    const briefRef = doc(db, 'users', userId, 'morningBriefs', date);
    const briefSnap = await getDoc(briefRef);

    if (!briefSnap.exists()) {
      return null; // Cache MISS
    }

    const cacheData = briefSnap.data();
    const generatedAt = cacheData.generatedAt as FirestoreTimestamp | undefined;

    if (!generatedAt?.seconds) {
      return null; // Cache MISS（寫入中或資料不完整）
    }

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.exists() ? userSnap.data() : {};
    const lastScheduleModifiedAt = userData.lastScheduleModifiedAt as FirestoreTimestamp | undefined;

    if (lastScheduleModifiedAt?.seconds && lastScheduleModifiedAt.seconds > generatedAt.seconds) {
      logger.dev('MorningKickoff', 'Cache stale，排程有變更', {
        generatedAt: generatedAt.seconds,
        lastScheduleModifiedAt: lastScheduleModifiedAt.seconds,
      });
      return null; // Stale → fallback
    }

    // Cache HIT
    return {
      narrative: cacheData.narrative || '',
      taskSuggestions: cacheData.taskSuggestions || [],
      suggestionOverallReason: cacheData.suggestionOverallReason || null,
      acceptedSuggestionKeys: cacheData.acceptedSuggestionKeys || [],
    };
  } catch (error) {
    logger.warn('MorningKickoff', 'Firestore cache 讀取失敗，fallback 至 Cloud Function', { error });
    return null;
  }
};

// API 調用函數
export const generateDailyKickoff = async (
  userId: string,
  date: string
): Promise<DailyKickoffResponse> => {
  const generateDailyKickoffFunction = httpsCallable(functions, 'generateDailyKickoff');
  const result = await generateDailyKickoffFunction({ userId, date });
  return result.data as DailyKickoffResponse;
};

export const adjustMorningSchedule = async (
  userId: string,
  date: string,
  adjustmentText: string
): Promise<AdjustScheduleResponse> => {
  const adjustMorningScheduleFunction = httpsCallable(functions, 'adjustMorningSchedule');
  const result = await adjustMorningScheduleFunction({
    userId,
    date,
    adjustmentText
  });
  return result.data as AdjustScheduleResponse;
};

/**
 * Phase 1.5: Parse Schedule Adjustment (Parse Only)
 * 解析排程調整指令 - 只解析不執行
 *
 * @param userId 用戶 ID
 * @param date 日期 (YYYY-MM-DD)
 * @param adjustmentText 調整指令（自然語言）
 * @returns 解析結果（包含結構化操作和衝突資訊）
 */
export const parseScheduleAdjustment = async (
  userId: string,
  date: string,
  adjustmentText: string
): Promise<ParseScheduleResponse> => {
  const parseScheduleAdjustmentFunction = httpsCallable(functions, 'parseScheduleAdjustment');
  const result = await parseScheduleAdjustmentFunction({
    userId,
    date,
    adjustmentText,
    language: 'zh'
  });
  return result.data as ParseScheduleResponse;
};

/**
 * 套用單一 AI 今日建議（create / move / delete）
 *
 * @param userId 用戶 ID
 * @param date 日期 (YYYY-MM-DD)
 * @param suggestion 要套用的 TaskSuggestion
 * @returns 套用結果
 */
export const applySuggestion = async (
  userId: string,
  date: string,
  suggestion: TaskSuggestion
): Promise<ApplySuggestionResponse> => {
  const applyScheduleChangesFunction = httpsCallable(functions, 'applyScheduleChanges');
  const result = await applyScheduleChangesFunction({
    userId,
    date,
    operations: [{ ...suggestion, title: suggestion.taskName }],
  });
  return result.data as ApplySuggestionResponse;
};

export const markSuggestionAccepted = async (
  date: string,
  suggestionKey: string
): Promise<void> => {
  const markSuggestionAcceptedFunction = httpsCallable(functions, 'markSuggestionAccepted');
  await markSuggestionAcceptedFunction({ date, suggestionKey });
};

export const applyScheduleChanges = async (
  userId: string,
  date: string,
  operations: ScheduleOperation[]
): Promise<ApplyScheduleResponse> => {
  const applyScheduleChangesFunction = httpsCallable(functions, 'applyScheduleChanges');
  const result = await applyScheduleChangesFunction({
    userId,
    date,
    operations
  });
  return result.data as ApplyScheduleResponse;
};
